import React, { useEffect, useMemo, useState } from "react";
import classes from "./PostsPage.module.css";
import Posts from "../components/Posts/Posts";
import { FaUserAlt } from "react-icons/fa";
import { MdTitle } from "react-icons/md";
import { BiCategory } from "react-icons/bi";
import { Pagination, Modal } from "react-bootstrap";
import { useData } from "../context/DataContext";
import { toast } from "react-toastify";

const FILTER_ACTIVE = "Active Posts";
const FILTER_PENDING = "Pending Posts";
const FILTER_SLIDER = "Slider Posts";

const PostsPage = () => {
  const {
    deleteLoading,
    postsData,
    setPostsData,
    deletePostById,
    fetchAndUpdatePendingPost,
    togglePostSlider,
  } = useData();
  const [loading] = useState(false);
  const [postsFilter, setPostsFilter] = useState(FILTER_ACTIVE);
  const [searchTitle, setSearchTitle] = useState("");
  const [searchUser, setSearchUser] = useState("");
  const [searchCategory, setSearchCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(25);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  const filteredPosts = useMemo(() => {
    const filtered = postsData.filter((post) => {
      const { title, uploader_details, category, is_pending, is_slider } = post;
      const lowerCaseTitle = title ? title.toLowerCase() : "";
      const lowerCaseUser = uploader_details?.name
        ? uploader_details?.name.toLowerCase()
        : "";
      const lowerCaseCategory = category ? category.toLowerCase() : "";

      const matchesSearch =
        (searchTitle === "" ||
          (lowerCaseTitle &&
            lowerCaseTitle.includes(searchTitle.toLowerCase()))) &&
        (searchUser === "" ||
          (lowerCaseUser &&
            lowerCaseUser.includes(searchUser.toLowerCase()))) &&
        (searchCategory === "" ||
          (lowerCaseCategory &&
            lowerCaseCategory.includes(searchCategory.toLowerCase())));

      if (!matchesSearch) return false;

      if (postsFilter === FILTER_PENDING) {
        return !!is_pending;
      }
      if (postsFilter === FILTER_SLIDER) {
        return !!is_slider && !is_pending;
      }
      return !is_pending && !is_slider;
    });

    if (postsFilter === FILTER_SLIDER) {
      filtered.sort(
        (a, b) => (a.slider_order || 0) - (b.slider_order || 0)
      );
    }

    return filtered;
  }, [postsData, searchTitle, searchUser, searchCategory, postsFilter]);

  const totalPages = Math.ceil(filteredPosts.length / postsPerPage) || 1;

  const pageNumbers = Array.from(
    { length: totalPages },
    (_, index) => index + 1
  );

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const currentPosts = filteredPosts.slice(
    (currentPage - 1) * postsPerPage,
    currentPage * postsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [searchTitle, searchUser, searchCategory, postsFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const toggleSelect = (postId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    const pageIds = currentPosts.map((post) => post.id);
    const allSelected =
      pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handlePostDelete = (post) => {
    setPostsData((prevPostData) =>
      prevPostData.filter((item) => item.id !== post.id)
    );
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(post.id);
      return next;
    });
    deletePostById(post.id);
  };

  const handleBulkDelete = async () => {
    const toDelete = filteredPosts.filter((post) => selectedIds.has(post.id));
    if (toDelete.length === 0) return;

    setShowBulkDeleteConfirm(false);
    setPostsData((prev) => prev.filter((post) => !selectedIds.has(post.id)));
    setSelectedIds(new Set());

    try {
      for (const post of toDelete) {
        await deletePostById(post.id, { silent: true });
      }
      toast.success(
        `Successfully deleted ${toDelete.length} post${
          toDelete.length === 1 ? "" : "s"
        }!`,
        {
          position: "bottom-left",
          autoClose: 5000,
          theme: "light",
        }
      );
    } catch (error) {
      console.error("Bulk delete failed:", error);
      toast.error("Some posts could not be deleted.", {
        position: "bottom-left",
        autoClose: 5000,
        theme: "light",
      });
    }
  };

  const handleClear = () => {
    setSearchTitle("");
    setSearchUser("");
    setSearchCategory("");
  };

  const handleDropdownChange = (event) => {
    setPostsFilter(event.target.value);
  };

  const showPendingPosts = postsFilter === FILTER_PENDING;
  const showSliderPosts = postsFilter === FILTER_SLIDER;
  const allOnPageSelected =
    currentPosts.length > 0 &&
    currentPosts.every((post) => selectedIds.has(post.id));

  return (
    <div className={classes.main}>
      <div className={"p-3 container "}>
        <h4 className={classes.mainText + " mb-3"}>
          <strong>Posts</strong>
        </h4>
        <h5>Search</h5>
        <nav className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div className={`input-group mb-3 ${classes.inp}`}>
            <span className="input-group-text">
              <MdTitle size={16} />
            </span>
            <input
              type="text"
              className="form-control "
              placeholder="Search Title"
              value={searchTitle}
              onChange={(event) => setSearchTitle(event.target.value)}
            />
          </div>
          <div className={`input-group mb-3 ${classes.inp}`}>
            <span className="input-group-text">
              <FaUserAlt size={16} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search User"
              value={searchUser}
              onChange={(event) => setSearchUser(event.target.value)}
            />
          </div>
          <div className={`input-group mb-3 ${classes.inp}`}>
            <span className="input-group-text">
              <BiCategory size={16} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search Category"
              value={searchCategory}
              onChange={(event) => setSearchCategory(event.target.value)}
            />
          </div>

          <div>
            <button
              className={`btn mb-3 ${classes.btnCustom}`}
              onClick={handleClear}
            >
              Clear
            </button>
          </div>
        </nav>
        <select value={postsFilter} onChange={handleDropdownChange}>
          <option value={FILTER_ACTIVE}>Active Posts</option>
          <option value={FILTER_PENDING}>Pending Posts</option>
          <option value={FILTER_SLIDER}>Slider Posts</option>
        </select>
        {loading && <div>Loading...</div>}
        {!loading && (
          <div className="d-flex justify-content-evenly align-items-center mt-4">
            <div className={"container"}>
              {currentPosts.length > 0 && (
                <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                  <label className="d-flex align-items-center gap-2 mb-0">
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={allOnPageSelected}
                      onChange={toggleSelectAllOnPage}
                    />
                    Select all on page
                  </label>
                  {selectedIds.size > 0 && (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      disabled={deleteLoading}
                      onClick={() => setShowBulkDeleteConfirm(true)}
                    >
                      Delete selected ({selectedIds.size})
                    </button>
                  )}
                </div>
              )}
              {currentPosts.length === 0 ? (
                <p className="text-muted">No posts found for this filter.</p>
              ) : (
                <Posts
                  postsList={currentPosts}
                  handlePostDelete={handlePostDelete}
                  showPendingPosts={showPendingPosts}
                  showSliderPosts={showSliderPosts}
                  handlePostApproval={fetchAndUpdatePendingPost}
                  handleToggleSlider={togglePostSlider}
                  deleteLoading={deleteLoading}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                />
              )}
            </div>
          </div>
        )}

        {!loading && (
          <div className="d-flex justify-content-center">
            <Pagination>
              {pageNumbers.map((pageNumber) => (
                <Pagination.Item
                  key={pageNumber}
                  active={currentPage === pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </Pagination.Item>
              ))}
            </Pagination>
          </div>
        )}
      </div>

      <Modal
        show={showBulkDeleteConfirm}
        onHide={() => setShowBulkDeleteConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Bulk Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{selectedIds.size}</strong> selected video
          {selectedIds.size === 1 ? "" : "s"}?
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={deleteLoading}
            onClick={() => setShowBulkDeleteConfirm(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={deleteLoading}
            onClick={handleBulkDelete}
          >
            Delete Selected
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
export default PostsPage;
